import { Car } from "lucide-react";
import { Image } from "./Image";
import { cn } from "../cn";

interface VehicleImageProps {
  readonly brand: string | null | undefined;
  readonly model: string | null | undefined;
  readonly color: string | null | undefined;
  /**
   * Photo of this vehicle, e.g. a damage photo attached to the dossier. When
   * present it replaces the colour swatch, since an actual picture says more
   * at a glance than a tinted glyph. Loading, shimmer and the missing-image
   * fallback are owned by {@link Image}.
   */
  readonly src?: string | null;
  /** Extra detail appended to the accessible label, e.g. the photo's intent. */
  readonly srcLabel?: string | null;
  readonly size?: "sm" | "lg";
  readonly className?: string;
}

const SIZE_STYLES: Record<
  NonNullable<VehicleImageProps["size"]>,
  { box: string; glyph: string }
> = {
  sm: { box: "h-8 w-11 rounded-md", glyph: "size-4" },
  lg: { box: "h-24 w-36 rounded-xl", glyph: "size-9" },
};

interface Paint {
  /** Swatch fill colour. */
  bg: string;
  /** Glyph colour, chosen for contrast against `bg`. */
  fg: string;
}

/** Fallback swatch for when the vehicle has no known colour. */
const NEUTRAL_PAINT: Paint = { bg: "#e2e5ea", fg: "#4b5563" };

/**
 * Resolve a vehicle's free-text paint description to a concrete swatch
 * colour. Vehicle data mixes English and Dutch names (e.g. "Mythos black",
 * "Parelmoer wit"), so this matches on the base-colour keyword rather than
 * the full string. The first matching entry wins, so more specific tones
 * precede generic ones. Colours are returned as inline styles on purpose:
 * Tailwind cannot generate classes from runtime-built strings, so a static
 * `bg-*` map would get purged.
 */
function resolvePaint(color: string | null | undefined): Paint {
  if (!color) {
    return NEUTRAL_PAINT;
  }

  const value = color.toLowerCase();
  const has = (...keywords: string[]): boolean =>
    keywords.some((keyword) => value.includes(keyword));

  if (has("wit", "white", "parel", "pearl")) {
    return { bg: "#eef1f4", fg: "#4b5563" };
  }
  if (has("zwart", "black")) {
    return { bg: "#1f2430", fg: "#e5e7eb" };
  }
  if (has("zilver", "silver")) {
    return { bg: "#c7ccd3", fg: "#3f4650" };
  }
  if (has("antraciet", "anthracite")) {
    return { bg: "#3a4150", fg: "#e5e7eb" };
  }
  if (has("grijs", "grey", "gray", "steel", "storm")) {
    return { bg: "#8b93a1", fg: "#ffffff" };
  }
  if (has("blauw", "blue", "vertigo", "royal", "midnight", "middernacht")) {
    return { bg: "#2f5fd0", fg: "#ffffff" };
  }
  if (has("rood", "red")) {
    return { bg: "#c8352e", fg: "#ffffff" };
  }
  if (has("groen", "green")) {
    return { bg: "#2f855a", fg: "#ffffff" };
  }
  if (has("champagne", "beige", "goud", "gold")) {
    return { bg: "#d8c39a", fg: "#5b503a" };
  }
  return NEUTRAL_PAINT;
}

/**
 * Compact, self-contained vehicle marker.
 *
 * Shows a photo of the vehicle when one is supplied through `src` — a dossier's
 * damage photo, for instance. Without a photo it renders a paint swatch tinted
 * to the vehicle's colour (or a neutral default when the colour is unknown)
 * with a car glyph, giving an at-a-glance sense of the vehicle without any
 * binary assets or network calls.
 *
 * Which photo to show is a domain decision and stays with the caller; this
 * primitive only frames it at the same dimensions as the swatch, so a header
 * keeps its height whether or not photos exist.
 */
export function VehicleImage({
  brand,
  model,
  color,
  src,
  srcLabel,
  size = "sm",
  className,
}: VehicleImageProps) {
  const paint = resolvePaint(color);
  const label = [brand, model, color].filter(Boolean).join(" · ") || "Voertuig";
  const styles = SIZE_STYLES[size];

  if (src) {
    return (
      <Image
        src={src}
        alt={srcLabel ? `${label} - ${srcLabel}` : label}
        title={label}
        className={cn(
          "shrink-0 ring-1 ring-black/15 dark:ring-white/15",
          styles.box,
          className,
        )}
      />
    );
  }

  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      style={{ backgroundColor: paint.bg, color: paint.fg }}
      className={cn(
        "grid shrink-0 place-items-center ring-1 ring-black/15 dark:ring-white/15",
        styles.box,
        className,
      )}
    >
      <Car className={styles.glyph} aria-hidden="true" />
    </span>
  );
}
